import { InfoCard } from '@backstage/core-components';
import {
  UserSettingsMenu,
  UserSettingsSignInAvatar,
  useUserProfile,
} from '@backstage/plugin-user-settings';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

/**
 * `group:entra-app-role/ksdp.admin` -> `entra-app-role/ksdp.admin`.
 * The `user:` ref (the person themselves) is dropped: the name and email
 * are already on the card.
 */
export function toAccessGroupNames(ownershipEntityRefs: string[]): string[] {
  return ownershipEntityRefs
    .filter(ref => ref.toLowerCase().startsWith('group:'))
    .map(ref => ref.slice('group:'.length))
    .sort((a, b) => a.localeCompare(b));
}

/**
 * The Settings "Profile" card, laid out like the built-in one, with the
 * user's access groups added to the email line as plain comma-separated
 * text: `name@kone.com, entra-app-role/ksdp.admin`.
 *
 * Plain text on purpose. The built-in Backstage Identity card showed the
 * same groups as links into the catalog entity pages, which are disabled for
 * KSDP users.
 */
export function KsdpProfileCard() {
  const { profile, displayName, backstageIdentity } = useUserProfile();

  const groups = toAccessGroupNames(
    backstageIdentity?.ownershipEntityRefs ?? [],
  );
  const secondLine = [profile.email, ...groups].filter(Boolean).join(', ');

  return (
    <InfoCard title="Profile" variant="gridItem">
      <Grid container spacing={6}>
        <Grid item>
          <UserSettingsSignInAvatar size={96} />
        </Grid>
        <Grid item xs={12} sm container>
          <Grid item xs container direction="column" spacing={2}>
            <Grid item xs>
              <Typography variant="subtitle1" gutterBottom>
                {displayName}
              </Typography>
              {secondLine && (
                <Typography variant="body2" color="textSecondary">
                  {secondLine}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <UserSettingsMenu />
          </Grid>
        </Grid>
      </Grid>
    </InfoCard>
  );
}
